import { watch, type Component } from 'vue'
import { useStorage, watchDebounced } from '@vueuse/core'
import { Building2, User } from 'lucide-vue-next'
import type { ListFilterCriterion, ListFilterOption } from '@/types/listFilters'
import useLocalStorage from '@/composables/useLocalStorage'
import useContentScope from '@/composables/contentScope'
import type {
  ContentScopeCreatorOption,
  ContentScopeFilters,
  ContentScopeOrigin,
  ContentScopeSubject
} from '@/types/contentScope'
import {
  contentScopeListParams,
  contentScopeParams,
  emptyContentScopeFilters,
  isContentScopeOrigin,
  sameContentScopeFilters
} from '@/utils/contentScope'

export default function useContentScopeList(
  subject: ContentScopeSubject,
  options: {
    fetchTotal: (params: Record<string, unknown>) => Promise<number>
    reload: () => unknown
    countsKey?: () => unknown
    storageKey?: string
  }
) {
  const { user } = useLocalStorage()
  const { canChooseScope, currentUserId } = useContentScope()

  const rememberedFilters = useStorage<PersistedContentScopeFilters>(
    `contentScope:filters:${user.value?.id}:${options.storageKey ?? subject}`,
    pickPersistedFilters(emptyContentScopeFilters()),
    localStorage,
    { mergeDefaults: true }
  )
  const sessionFilters = ref<Pick<ContentScopeFilters, 'search' | 'statuses'>>({ search: '', statuses: [] })

  const filters = computed<ContentScopeFilters>(() => ({
    ...pickPersistedFilters(rememberedFilters.value),
    ...sessionFilters.value
  }))

  const replaceFilters = (next: ContentScopeFilters) => {
    rememberedFilters.value = pickPersistedFilters(next)
    sessionFilters.value = { search: next.search, statuses: next.statuses }
  }

  const params = computed(() => contentScopeParams(filters.value))

  const isMineView = computed(() => {
    const id = currentUserId.value
    if (id == null) return false
    return sameContentScopeFilters(filters.value, { ...emptyContentScopeFilters(), creators: [id] })
  })
  const hasActiveFilters = computed(() => !sameContentScopeFilters(filters.value, emptyContentScopeFilters()))

  const setFilters = (patch: Partial<ContentScopeFilters>) => {
    replaceFilters({ ...filters.value, ...patch })
  }

  const resetFilters = () => {
    replaceFilters(emptyContentScopeFilters())
  }

  const COUNTS_TTL_MS = 30_000
  const countCache = new Map<string, { at: number; request: Promise<void> }>()
  const loadOnce = (name: string, load: () => Promise<void>) => {
    const key = `${JSON.stringify(options.countsKey?.() ?? null)}:${name}`
    const cached = countCache.get(key)
    if (cached && Date.now() - cached.at < COUNTS_TTL_MS) return cached.request
    const request = load()
    countCache.set(key, { at: Date.now(), request })
    return request
  }

  const invalidateCountCaches = () => {
    countCache.clear()
    originCountsByFilters.value = {}
  }

  if (options.countsKey) {
    watch(options.countsKey, invalidateCountCaches, { deep: true })
  }

  const originCountsByFilters = ref<Record<string, Partial<Record<ContentScopeOrigin, number | null>>>>({})
  const originKey = computed(() => JSON.stringify({ ...filters.value, origin: undefined }))
  const originCounts = computed(() => originCountsByFilters.value[originKey.value] ?? {})
  const loadOriginCounts = () => {
    const key = originKey.value
    const current = { ...filters.value }
    return loadOnce(`origins:${key}`, async () => {
      const origins: ContentScopeOrigin[] = [
        'all',
        'network',
        'local',
        ...(canChooseScope.value ? ['unclassified' as const] : [])
      ]
      const entries = await Promise.all(
        origins.map(async (origin) => [
          origin,
          await options.fetchTotal(contentScopeListParams({ ...current, origin })).catch(() => null)
        ])
      )
      originCountsByFilters.value[key] = Object.fromEntries(entries)
    })
  }

  let suspendReload = false
  const withoutReload = (run: () => void) => {
    suspendReload = true
    try {
      run()
    } finally {
      suspendReload = false
    }
  }

  watch(
    filters,
    () => {
      if (suspendReload) return
      options.reload()
    },
    { deep: true }
  )

  return {
    subject,
    filters,
    params,
    isMineView,
    hasActiveFilters,
    setFilters,
    resetFilters,
    canChooseScope,
    originCounts,
    loadOriginCounts,
    withoutReload
  }
}

export type ContentScopeListState = ReturnType<typeof useContentScopeList>

type PersistedContentScopeFilters = Pick<ContentScopeFilters, 'origin' | 'creators' | 'pongoTeam'>

const pickPersistedFilters = ({ origin, creators, pongoTeam }: PersistedContentScopeFilters) => ({
  origin,
  creators,
  pongoTeam
})

const PONGO_TEAM = 'pongo-team'

type Translate = (key: string) => string

const buildOriginCriterion = (state: ContentScopeListState, t: Translate): ListFilterCriterion => {
  const current = state.filters.value.origin
  const origins: ContentScopeOrigin[] = [
    'network',
    'local',
    ...(state.canChooseScope.value ? ['unclassified' as const] : [])
  ]

  return {
    key: 'origin',
    label: t('contentScope.filters.origin'),
    icon: Building2,
    options: origins.map((value) => ({
      value,
      label: t(`contentScope.scopes.${value}`),
      count: state.originCounts.value[value] ?? null
    })),
    selected: current === 'all' ? [] : [current],
    multiple: false,
    toggle: (value) => {
      if (!isContentScopeOrigin(value)) return
      state.setFilters({ origin: value })
    },
    clear: () => state.setFilters({ origin: 'all' }),
    onOpen: () => state.loadOriginCounts()
  }
}

const buildCreatedByCriterion = ({
  state,
  t,
  creatorOptions,
  currentUserId,
  loadCreatorOptions
}: {
  state: ContentScopeListState
  t: Translate
  creatorOptions: ContentScopeCreatorOption[]
  currentUserId: number | undefined
  loadCreatorOptions: () => Promise<void>
}): ListFilterCriterion => {
  const { creators, pongoTeam } = state.filters.value
  const options = [...creatorOptions].sort((a, b) => {
    if (a.id === currentUserId) return -1
    if (b.id === currentUserId) return 1
    return a.name.localeCompare(b.name)
  })

  return {
    key: 'createdBy',
    label: t('contentScope.filters.createdBy'),
    icon: User,
    options: [
      ...options.map((option) => ({
        value: String(option.id),
        label: option.id === currentUserId ? t('contentScope.creator.you') : option.name,
        hint: option.role ?? t('contentScope.creator.roleUndefined')
      })),
      { value: PONGO_TEAM, label: t('contentScope.creator.support') }
    ],
    searchPlaceholder: t('contentScope.filters.searchPerson'),
    emptyText: t('contentScope.filters.noPerson'),
    selected: [...creators.map(String), ...(pongoTeam ? [PONGO_TEAM] : [])],
    multiple: true,
    searchable: true,
    toggle: (value) => {
      if (value === PONGO_TEAM) {
        state.setFilters({ pongoTeam: !pongoTeam })
        return
      }
      const id = Number(value)
      state.setFilters({
        creators: creators.includes(id) ? creators.filter((creator) => creator !== id) : [...creators, id]
      })
    },
    clear: () => state.setFilters({ creators: [], pongoTeam: false }),
    onOpen: loadCreatorOptions
  }
}

export function useContentScopeCriteria(state: ContentScopeListState) {
  const { t } = useI18n()
  const { fetchCreatorOptions, currentUserId } = useContentScope()

  const creatorOptions = ref<ContentScopeCreatorOption[]>([])
  let creatorOptionsRequest: Promise<void> | null = null
  const loadCreatorOptions = () => {
    creatorOptionsRequest ??= fetchCreatorOptions().then((options) => {
      creatorOptions.value = options
    })
    return creatorOptionsRequest
  }

  const hasSelectedCreators = state.filters.value.creators.length > 0
  if (hasSelectedCreators) loadCreatorOptions()

  const origin = computed(() => buildOriginCriterion(state, t))
  const createdBy = computed(() =>
    buildCreatedByCriterion({
      state,
      t,
      creatorOptions: creatorOptions.value,
      currentUserId: currentUserId.value,
      loadCreatorOptions
    })
  )

  return { origin, createdBy }
}

export function useContentScopeStatusCriterion(
  state: ContentScopeListState,
  config: () => { label: string; icon: Component; options: ListFilterOption[]; multiple: boolean }
) {
  return computed<ListFilterCriterion>(() => {
    const statuses = state.filters.value.statuses
    const { label, icon, options, multiple } = config()

    const next = (value: string) => {
      if (!multiple) return [value]
      return statuses.includes(value) ? statuses.filter((status) => status !== value) : [...statuses, value]
    }

    return {
      key: 'status',
      label,
      icon,
      options,
      selected: statuses,
      multiple,
      toggle: (value) => state.setFilters({ statuses: next(value) }),
      clear: () => state.setFilters({ statuses: [] })
    }
  })
}

export const useContentScopeSearch = (state: ContentScopeListState, delay = 400) => {
  const searchText = ref(state.filters.value.search)

  watch(
    () => state.filters.value.search,
    (value) => {
      if (searchText.value !== value) searchText.value = value
    }
  )

  watchDebounced(
    searchText,
    (value) => {
      const next = value ?? ''
      if (next !== state.filters.value.search) state.setFilters({ search: next })
    },
    { debounce: delay }
  )

  return { searchText }
}
